import React, { Fragment, useEffect, useRef, useState, type ReactNode } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import Close from '@jwp/ott-theme/assets/icons/close.svg?react';

import IconButton from '../IconButton/IconButton';
import Icon from '../Icon/Icon';
import scrollbarSize from '../../utils/dom';

import styles from './Sidebar.module.scss';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';
import {
  RELATIVE_PATH_USER_PAYMENTS,
  RELATIVE_PATH_USER_PRIVACY_POLICY,
  RELATIVE_PATH_USER_SETTINGS,
  RELATIVE_PATH_USER_SIGN_OUT,
  RELATIVE_PATH_USER_TERMS_OF_USE,
} from '@jwp/ott-common/src/paths';
import { useLocation, useNavigate } from 'react-router';
import { modalURLFromLocation } from '../../utils/location';
import CustomMenuButton from '../CustomMenuButton/CustomMenuButton';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { useUIControls } from '@jwp/ott-hooks-react/src/useUIControls';
import { APP_RELEASE_VERSION } from '../../../../../platforms/web/src/config/clientEnvConfig';
import { useTheme } from '../../../../../ThemeContext';

type SidebarProps = {
  isOpen: boolean;
  onClose: () => void;
  children?: ReactNode;
  showBorder?: boolean;
};

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose, children, showBorder }) => {
  const { t } = useTranslation(['menu', 'user']);
  const navigate = useNavigate();
  const location = useLocation();
  const { universalStyles } = useTheme();
  const lastFocusedElementRef = useRef<HTMLElement | null>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const {
    canShowTermsOfUse,
    canShowPrivacyPolicy,
    isSubscriptionEnabled,
    canShowExportData,
    canShowSettings,
    isAuthenticationEnabled,
    isGuestModeEnabled,
    guestStreamingEnabled,
    isQrAuthenticationEnabled,
  } = useAppConfig();
  const appVersion = APP_RELEASE_VERSION;

  const { redirectionPolicyURL, redirectionTosURL } = useUIControls();
  const htmlAttributes = { inert: !isOpen ? '' : undefined }; // inert is not yet officially supported in react. see: https://github.com/facebook/react/pull/24730
  const isLoggedIn = !!useAccountStore(({ user }) => user);

  const shouldShowProfileForGuestMode = !isAuthenticationEnabled && isGuestModeEnabled && guestStreamingEnabled && !isQrAuthenticationEnabled;

  useEffect(() => {
    if (isOpen) {
      // Before inert on the body is applied in Layout, we need to set this ref
      lastFocusedElementRef.current = document.activeElement as HTMLElement;

      // When opened, adjust the margin-right to accommodate for the scrollbar width to prevent UI shifts in background
      document.body.style.marginRight = `${scrollbarSize()}px`;
      document.body.style.overflowY = 'hidden';

      // Scroll the sidebar to the top if the user has previously scrolled down in the sidebar
      if (sidebarRef.current) {
        sidebarRef.current.scrollTop = 0;
      }
    } else {
      document.body.style.removeProperty('margin-right');
      document.body.style.removeProperty('overflow-y');
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (isOpen && event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscKey);

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    const sidebarElement = sidebarRef.current;
    const handleTransitionEnd = () => {
      setVisible(isOpen);
    };

    sidebarElement?.addEventListener('transitionend', handleTransitionEnd);

    return () => {
      sidebarElement?.removeEventListener('transitionend', handleTransitionEnd);
    };
  }, [isOpen]);

  useEffect(() => {
    if (visible) {
      sidebarRef.current?.querySelectorAll('a')[0]?.focus({ preventScroll: true });
    } else {
      lastFocusedElementRef.current?.focus({ preventScroll: true });
    }
  }, [visible]);

  const customMenu: any[] = [
    {
      label: t('user:nav.my_subscription'),
      to: `/u/${RELATIVE_PATH_USER_PAYMENTS}`,
      required: isSubscriptionEnabled,
    },
    {
      label: t('user:nav.export_data'),
      required: canShowExportData,
      onClick: () => navigate(modalURLFromLocation(location, 'export-data'), { replace: true }),
    },
    {
      label: t('user:nav.terms_of_use'),
      to: redirectionTosURL || `/u/${RELATIVE_PATH_USER_TERMS_OF_USE}`,
      ...(redirectionTosURL ? { target: '_blank', rel: 'noreferrer' } : {}),
      required: canShowTermsOfUse,
    },
    {
      label: t('user:nav.privacy_policy'),
      to: redirectionPolicyURL || `/u/${RELATIVE_PATH_USER_PRIVACY_POLICY}`,
      ...(redirectionPolicyURL ? { target: '_blank', rel: 'noreferrer' } : {}),
      required: canShowPrivacyPolicy,
    },

    {
      label: t('user:nav.settings'),
      to: `/u/${RELATIVE_PATH_USER_SETTINGS}`,
      required: canShowSettings,
    },
    {
      label: t('user:nav.signout'),
      to: `/u/${RELATIVE_PATH_USER_SIGN_OUT}`,
      required: !shouldShowProfileForGuestMode ? true : false,
    },
  ]?.filter((e) => e.required);

  const isMultipleMenu = customMenu?.length > 0 && (isLoggedIn || shouldShowProfileForGuestMode);

  return (
    <Fragment>
      <div
        className={classNames(styles.backdrop, {
          [styles.visible]: isOpen,
        })}
        onClick={onClose}
      />
      <div
        ref={sidebarRef}
        className={classNames(styles.sidebar, {
          [styles.open]: isOpen,
        })}
        id="sidebar"
        {...htmlAttributes}
      >
        <div className={styles.heading}>
          <IconButton onClick={onClose} aria-label={t('menu:close_menu')}>
            <Icon icon={Close} />
          </IconButton>
        </div>
        <nav className={`styles.group ${showBorder || isMultipleMenu ? '' : styles.singleMenu}`} onClick={onClose}>
          {children}
          <ul>
            {(isLoggedIn || shouldShowProfileForGuestMode) &&
              customMenu?.map(({ label, to, onClick, ...rest }) => (
                <li key={label} className={styles.listItem}>
                  {!to ? <CustomMenuButton label={label} onClick={onClick} {...rest} /> : <CustomMenuButton label={label} to={to} {...rest} />}
                </li>
              ))}
          </ul>
        </nav>
        <div className={styles.version} style={universalStyles.footerText}>
          Version {appVersion}
        </div>
      </div>
    </Fragment>
  );
};

export default Sidebar;
